import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import themeContext from "../../../../../../context/theme/ThemeContext";
import CheckMark from "../../../../../../components/SVGComponents/Tools/CheckMark";
import HPIcon from "../../../../../../components/SVGComponents/Tools/HPIcon";
import Reset from "../../../../../../components/SVGComponents/Tools/Reset";
import SettingIcon from "../../../../../../components/SVGComponents/SettingIcon";
import userContext from "../../../../../../context/users/UserContext";
import { USERTYPES } from "../../../../../../constants/Constants";
import { GROUP_SCOPE } from "../../../../../../helpers/hangingProtocol/protocolEnums";

/**
 * The viewer's "choose a hanging protocol" menu.
 *
 * Shared by the toolbar split button and the "..." menu's HP flyout so both
 * list the same thing. Only protocols whose conditions match the open study are
 * listed; the rest are left out rather than shown greyed, so the menu stays
 * short and every row in it is clickable. When nothing matches, the menu says so.
 *
 * Laid out like the advanced viewer's "Hanging protocols list" panel: a plain
 * titled panel, a narrow icon column, and rows that run edge to edge so the
 * protocol name is the thing you read. Only the geometry is borrowed from that
 * viewer — every colour here still comes from this viewer's own theme.
 */

/** Grey for rows that cannot be clicked — dimmer than the inactive tool colour. */
const DISABLED_COLOR = "#7A7A7A";

const HangingProtocolMenu = ({
  candidates = [],
  appliedKey = null,
  onSelect,
  onReset,
  onManage,
  resetDisabled = false,
}) => {
  const { t } = useTranslation();
  const { theme } = useContext(themeContext);
  const { roles = [] } = useContext(userContext) || {};
  const [hoveredItem, setHoveredItem] = useState(null);

  // Who gets the "Manage protocols" row: the row leaves the viewer for the HP
  // authoring screens, and `roleAccess` only lets the super admin and radiologists
  // open those — for any other role the link would land on the not-allowed page, so
  // the row is left out rather than shown and then refused. A share link carries no
  // role at all and is treated the same.
  //
  // Read inside the component, not at module level: Constants.jsx imports the viewer
  // tree, so this module can be evaluated before USERTYPES is initialised.
  const canManageProtocols =
    roles[0] === USERTYPES.ADMIN || roles[0] === USERTYPES.RADIOLOGIST;

  // Unmatched protocols are dropped entirely — the menu only offers rows the
  // user can actually apply.
  const applicable = candidates.filter((candidate) => candidate.applicable);

  // The radiologist's own protocols, then the shared ones, with a rule between them. The
  // two are authored in different places and by different people, and a list that runs
  // them together gives no hint which is which — the "XRAY" a radiologist wrote reads
  // exactly like the "All" an admin published.
  //
  // Order within each half is left alone, so precedence still reads top-down; only the
  // halves are pulled apart. A read that carries no scope at all — a share link, which is
  // served the shared set and nothing personal — lands wholly in `shared`, and the rule is
  // dropped whenever either half is empty, so a user with only their own protocols (or
  // only inherited ones) sees the same single list as before.
  const mine = applicable.filter((candidate) => candidate.scope === GROUP_SCOPE.USER);
  const shared = applicable.filter((candidate) => candidate.scope !== GROUP_SCOPE.USER);

  // "Non Xray | CT Viewport Matching" — group then protocol, as the advanced
  // viewer lists them. v1 protocols have no group, so they stay bare rather
  // than showing a dangling separator.
  const rowLabel = (candidate) =>
    candidate.groupName ? `${candidate.groupName} | ${candidate.name}` : candidate.name;

  // Geometry lives in .hp-menu-row; only the colours are decided here, so the
  // active/hover tints keep coming from the theme palette.
  const textColor = (key, disabled, active) => {
    if (disabled) return DISABLED_COLOR;
    if (active || hoveredItem === key) return theme.palette.activeTool;
    return "inherit";
  };

  const iconColor = (key, disabled, active) => {
    if (disabled) return DISABLED_COLOR;
    if (active || hoveredItem === key) return theme.palette.activeTool;
    return theme.palette.inactiveTool;
  };

  const renderRow = ({ key, icon, label, badge, disabled, active, onClick, title }) => (
    <div
      key={key}
      className={`hp-menu-row${disabled ? " hp-menu-row-disabled" : ""}`}
      style={{ color: textColor(key, disabled, active) }}
      title={title}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHoveredItem(key)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <span className="hp-menu-icon">{icon}</span>
      <span className="hp-menu-name">{label}</span>
      {badge && (
        <span
          className="hp-menu-badge"
          style={{ color: disabled ? DISABLED_COLOR : active ? theme.palette.activeTool : undefined }}
        >
          {badge}
        </span>
      )}
    </div>
  );

  const renderCandidate = (candidate) => {
    const isApplied = appliedKey != null && candidate.key === appliedKey;
    return renderRow({
      key: candidate.key,
      // A tick in the icon column marks the live protocol — same box as
      // the HP glyph, so the label column never shifts.
      icon: isApplied ? (
        <CheckMark color={theme.palette.activeTool} />
      ) : (
        <HPIcon color={iconColor(candidate.key, false, false)} />
      ),
      label: rowLabel(candidate),
      badge: `${Math.round(candidate.matchPercentage)}%`,
      active: isApplied,
      onClick: () => onSelect?.(candidate),
      title: candidate.description || rowLabel(candidate),
    });
  };

  return (
    <div className="hp-menu-panel">
      <div className="hp-menu-header">
        {t("hanging-protocol.menu-title", "Hanging protocols list")}
      </div>

      {candidates.length === 0 && (
        <div className="hp-menu-empty">
          {t("hanging-protocol.none-configured", "No hanging protocols configured")}
        </div>
      )}

      {candidates.length > 0 && applicable.length === 0 && (
        <div className="hp-menu-empty">{t("hanging-protocol.no-suitable-protocol")}</div>
      )}

      {applicable.length > 0 && (
        <div className="hp-menu-list">
          {mine.map(renderCandidate)}
          {mine.length > 0 && shared.length > 0 && <div className="hp-menu-separator" />}
          {shared.map(renderCandidate)}
        </div>
      )}

      <div className="hp-menu-separator" />

      <div className="hp-menu-actions">
        {renderRow({
          key: "hp-reset",
          icon: <Reset color={iconColor("hp-reset", resetDisabled, false)} />,
          label: t("hanging-protocol.reset-layout", "Reset to default layout (1×1)"),
          disabled: resetDisabled,
          onClick: () => onReset?.(),
        })}

        {canManageProtocols &&
          renderRow({
            key: "hp-manage",
            icon: <SettingIcon active={hoveredItem === "hp-manage"} />,
            label: t("hanging-protocol.manage", "Manage protocols"),
            onClick: () => onManage?.(),
          })}
      </div>
    </div>
  );
};

export default HangingProtocolMenu;
